import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { DataTableButton, DataTableFilters, DataTableSelect } from "../../components/data-table";
import AlertNotification from "../../components/notification";
import AddProjectModal from "../../components/project-management/AddProjectModal";
import ProjectTable from "../../components/project-management/ProjectTable";
import DeleteModal from "../../components/models/deleteModal.jsx";
import api from "../../services/apiService";
import { ROLES } from "../../constants";
import { getUserProfile } from "../../controllers/utilityController";
import { mapApiProjectToRecord, mapProjectRecord } from "./projectMockData";

const fallbackProjectTypeToIdMap = {
  Internal: 1,
  "Client Delivery": 2,
  Automation: 3,
  Compliance: 4,
  Marketing: 5,
  "Data Engineering": 6,
  Content: 7,
  Analytics: 8,
  Growth: 9,
  AI: 10,
};

const fallbackProjectTypes = Object.keys(fallbackProjectTypeToIdMap);

function ProjectManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [modalInstanceKey, setModalInstanceKey] = useState(0);
  const [notification, setNotification] = useState(null);

  // Dashboard quick action: open the create modal, then clear the flag so a
  // refresh or back-navigation doesn't reopen it.
  useEffect(() => {
    if (!location.state?.openCreate) return;
    setEditingProject(null);
    setModalInstanceKey((prev) => prev + 1);
    setShowAddProjectModal(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [projectTypeOptions, setProjectTypeOptions] =
    useState(fallbackProjectTypes);
  const [projectTypeIdByName, setProjectTypeIdByName] = useState(
    fallbackProjectTypeToIdMap,
  );
  const [statusFilter, setStatusFilter] = useState("active");
  const [projectTypeFilter, setProjectTypeFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [role, setRole] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getUserProfile();
        setRole(res?.role || "");
      } catch (error) {
        console.error("Failed to fetch user profile", error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get("/fetch-users");
        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        setFetchedUsers(list);
        const emails = [
          ...new Set(list.map((user) => user?.email).filter(Boolean)),
        ];
        if (emails.length > 0) {
          setAssigneeOptions(emails);
        }
      } catch (error) {
        console.error("Failed to fetch users for assignee list", error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/projects", {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });

        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        if (list.length > 0) {
          setProjects(
            list.map((project) =>
              mapProjectRecord(mapApiProjectToRecord(project)),
            ),
          );
        }
      } catch (error) {
        console.error("Failed to fetch projects", error);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/project-types", {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });

        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        const normalized = list
          .map((item) => {
            const id = Number(item?.id ?? item?.projectTypeId);
            const label =
              item?.name ||
              item?.projectType ||
              item?.projectTypeName ||
              item?.type ||
              item?.title ||
              "";

            return {
              id,
              label: String(label).trim(),
            };
          })
          .filter((item) => Number.isFinite(item.id) && item.label);

        if (normalized.length > 0) {
          const idMap = normalized.reduce((acc, item) => {
            acc[item.label] = item.id;
            return acc;
          }, {});

          setProjectTypeOptions(normalized.map((item) => item.label));
          setProjectTypeIdByName(idMap);
        }
      } catch (error) {
        console.error("Failed to fetch project types", error);
      }
    };

    fetchProjectTypes();
  }, []);

  const closeProjectModal = () => {
    setShowAddProjectModal(false);
    setEditingProject(null);
  };

  const createNextProjectId = () => {
    const numbers = projects
      .map((project) => parseInt(project.projectId.replace(/\D/g, ""), 10))
      .filter((value) => Number.isFinite(value));

    const nextValue = (numbers.length ? Math.max(...numbers) : 1000) + 1;
    return `PRJ-${String(nextValue).padStart(4, "0")}`;
  };

  const handleCreateProject = async (formValues) => {
    if (editingProject) {
      const totalTasks = Number(formValues.targets) || 0;
      const existingCompleted = editingProject.completedTasks || 0;
      const completedTasks =
        formValues.status === "Completed"
          ? totalTasks
          : Math.min(existingCompleted, totalTasks);

      const assigneeIds = fetchedUsers
        .filter(
          (user) =>
            formValues.assignedUsers.includes(user?.email) &&
            Number.isFinite(Number(user?.id)),
        )
        .map((user) => Number(user.id));

      const payload = {
        projectName: formValues.projectName,
        projectTypeId:
          projectTypeIdByName[formValues.projectType] ||
          fallbackProjectTypeToIdMap[formValues.projectType] ||
          1,
        status: formValues.status,
        priority: formValues.priority,
        targets: Number(formValues.targets) || 0,
        startDate: formValues.startDate,
        dueDate: formValues.dueDate,
        notes: formValues.notes || "",
        assigneeIds,
      };

      const editingBackendId = Number(editingProject.backendId);
      if (!Number.isFinite(editingBackendId)) {
        setNotification({
          text: "Missing project id for update.",
          status: false,
        });
        return;
      }

      try {
        const token = localStorage.getItem("token");
        await api.put(`/projects/${editingBackendId}`, payload, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
      } catch (error) {
        console.error("Failed to update project", error);
        setNotification({ text: "Failed to update project.", status: false });
        return;
      }

      const updatedRecord = {
        id: editingProject.projectId,
        backendId: editingBackendId,
        name: formValues.projectName,
        type: formValues.projectType,
        status: formValues.status,
        priority: formValues.priority,
        completedTasks,
        totalTasks,
        targets: payload.targets,
        startDate: formValues.startDate,
        dueDate: formValues.dueDate,
        assignedUsers: formValues.assignedUsers,
        notes: formValues.notes,
      };

      setProjects((prev) =>
        prev.map((project) =>
          project.projectId === editingProject.projectId
            ? mapProjectRecord(updatedRecord)
            : project,
        ),
      );
      closeProjectModal();
      setNotification({ text: "Project updated successfully!", status: true });
      return;
    }

    const assigneeIds = fetchedUsers
      .filter(
        (user) =>
          formValues.assignedUsers.includes(user?.email) &&
          Number.isFinite(Number(user?.id)),
      )
      .map((user) => Number(user.id));

    const payload = {
      projectCode: createNextProjectId(),
      projectName: formValues.projectName,
      projectTypeId:
        projectTypeIdByName[formValues.projectType] ||
        fallbackProjectTypeToIdMap[formValues.projectType] ||
        1,
      status: formValues.status,
      priority: formValues.priority,
      targets: Number(formValues.targets) || 0,
      startDate: formValues.startDate,
      dueDate: formValues.dueDate,
      notes: formValues.notes || "",
      assigneeIds,
    };

    let createdProject = null;
    try {
      const token = localStorage.getItem("token");
      const response = await api.post("/projects", payload, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      createdProject = response?.data?.data || response?.data || null;
    } catch (error) {
      console.error("Failed to create project", error);
      setNotification({ text: "Failed to create project.", status: false });
      return;
    }

    const nextRecord = {
      id: createdProject?.projectCode || payload.projectCode,
      backendId: Number.isFinite(Number(createdProject?.id))
        ? Number(createdProject.id)
        : null,
      name: formValues.projectName,
      type: formValues.projectType,
      status: formValues.status,
      priority: formValues.priority,
      completedTasks: formValues.status === "Completed" ? payload.targets : 0,
      totalTasks: payload.targets,
      targets: payload.targets,
      startDate: formValues.startDate,
      dueDate: formValues.dueDate,
      assignedUsers: formValues.assignedUsers,
      notes: formValues.notes,
    };

    setProjects((prev) => [mapProjectRecord(nextRecord), ...prev]);
    closeProjectModal();
    setNotification({ text: "Project created successfully!", status: true });
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setModalInstanceKey((prev) => prev + 1);
    setShowAddProjectModal(true);
  };

  const handleOpenProjectBoard = (project) => {
    const routeProjectId = project.backendId || project.projectId;
    navigate(`/admin/project-management/${routeProjectId}`, {
      state: { project },
    });
  };

  const handleDeleteProject = async (project) => {
    if (role !== ROLES.ADMIN) return;

    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (isDeletingProject) return;
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const handleConfirmDeleteProject = async () => {
    if (!projectToDelete) return;

    const backendId = Number(projectToDelete?.backendId);
    if (!Number.isFinite(backendId)) {
      setNotification({
        text: "Missing project id for delete.",
        status: false,
      });
      handleCloseDeleteModal();
      return;
    }

    setIsDeletingProject(true);
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/projects/${backendId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      setProjects((prev) =>
        prev.filter((item) => item.projectId !== projectToDelete.projectId),
      );
      setNotification({ text: "Project deleted successfully!", status: true });
      setShowDeleteModal(false);
      setProjectToDelete(null);
    } catch (error) {
      console.error("Failed to delete project", error);
      setNotification({ text: "Failed to delete project.", status: false });
    } finally {
      setIsDeletingProject(false);
    }
  };

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchesStatus =
        statusFilter === "all"
          ? true
          : String(project?.status || "")
              .trim()
              .toLowerCase() === statusFilter;

      const matchesProjectType =
        projectTypeFilter === "all"
          ? true
          : String(project?.projectType || "").trim() === projectTypeFilter;

      const matchesAssignee =
        assigneeFilter === "all"
          ? true
          : Array.isArray(project?.assignedUsers) &&
            project.assignedUsers.includes(assigneeFilter);

      return matchesStatus && matchesProjectType && matchesAssignee;
    });
  }, [projects, statusFilter, projectTypeFilter, assigneeFilter]);
  return (
    <div className="w-full max-w-full">
      <ProjectTable
        projects={filteredProjects}
        loading={isLoadingProjects}
        onEditProject={handleEditProject}
        onOpenProjectBoard={handleOpenProjectBoard}
        onDeleteProject={handleDeleteProject}
        canDelete={role === ROLES.ADMIN}
        // "active" is the page's default view, so it doesn't count as an applied filter.
        activeFilterCount={
          [statusFilter !== "active", projectTypeFilter !== "all", assigneeFilter !== "all"].filter(Boolean).length
        }
        filterContent={
          <DataTableFilters
            canClear={statusFilter !== "active" || projectTypeFilter !== "all" || assigneeFilter !== "all"}
            onClear={() => {
              setStatusFilter("active");
              setProjectTypeFilter("all");
              setAssigneeFilter("all");
            }}
          >
            <DataTableSelect
              id="projects-status-filter"
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "active", label: "Active" },
                { value: "closed", label: "Closed" },
              ]}
            />
            <DataTableSelect
              id="projects-type-filter"
              label="Project Type"
              value={projectTypeFilter}
              onChange={setProjectTypeFilter}
              options={[
                { value: "all", label: "All" },
                ...projectTypeOptions.map((type) => ({ value: type, label: type })),
              ]}
            />
            <DataTableSelect
              id="projects-assignee-filter"
              label="Assigned User"
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              options={[
                { value: "all", label: "All" },
                ...assigneeOptions.map((user) => ({ value: user, label: user })),
              ]}
            />
          </DataTableFilters>
        }
        toolbarRight={
          <DataTableButton
            variant="primary"
            icon={Plus}
            onClick={() => {
              setEditingProject(null);
              setModalInstanceKey((prev) => prev + 1);
              setShowAddProjectModal(true);
            }}
          >
            Add Project
          </DataTableButton>
        }
      />

      <AddProjectModal
        key={modalInstanceKey}
        show={showAddProjectModal}
        onClose={closeProjectModal}
        onCreateProject={handleCreateProject}
        users={assigneeOptions}
        projectTypes={projectTypeOptions}
        mode={editingProject ? "edit" : "add"}
        initialProject={editingProject}
      />

      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleConfirm={handleConfirmDeleteProject}
        message="Are you sure you want to delete this project? This action will permanently delete the project and all associated recruits. This data cannot be recovered."
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeletingProject}
      />

      {notification && (
        <AlertNotification
          text={notification.text}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}

export default ProjectManagement;
